import moment from "moment";
import vars from "utils/vars";
import { parsePatientDateOfBirthMoment } from "./patientDate";
export * from "./abilities";
export * from "./animateCss";
export * from "./api";
export * from "./apiDate";
export * from "./appointment";
export * from "./notification";
export * from "./bcMinorAilmentScreening";
export * from "./buildJustmedsPatientIntakeDataFromGlobals";
export * from "./categories";
export * from "./currency-symbol-map";
export * from "./dom";
export * from "./favicon";
export * from "./form";
export * from "./justmedsConsultationDocuments";
export * from "./justmedsIframePostMessage";
export * from "./isolate-members-access";
export * from "./modal";
export * from "./patient-facing-url-warning";
export * from "./patient-intake-subjective-optional";
export * from "./patientIntakeClock";
export * from "./patientIntakePayment";
export * from "./patientLookup";
export * from "./patientDate";
export * from "./note";
export * from "./pdfGenerator";
export * from "./pdfSaveToDisk";
export * from "./questionnairePdf";
export * from "./notePatientDisplay";
export * from "./noteSubmit";
export * from "./regexp";
export * from "./sourceAttribution";
export * from "./segments";
export * from "./tenant";
export * from "./termsConditionsPolicy";
export * from "./tokenHandler";
export * from "./user";
export * from "./white-label";

export const discount = (number, fixed = 0) => Number.parseFloat(number).toFixed(fixed);

export const flattenDeep = (arr = []) =>
	Array.isArray(arr) ? arr.reduce((a, b) => a.concat(flattenDeep(b)), []) : [arr];

export const debounce = (func, wait, immediate) => {
	var timeout;
	return function () {
		var context = this,
			args = arguments;
		clearTimeout(timeout);
		timeout = setTimeout(function () {
			timeout = null;
			if (!immediate) func.apply(context, args);
		}, wait);
		if (immediate && !timeout) func.apply(context, args);
	};
};

export const debounceAsync = (func, waitMs) => {
	let timeout = null;

	return (...args) => {
		clearTimeout(timeout);
		return new Promise((resolve) => {
			timeout = setTimeout(() => resolve(func(...args)), waitMs);
		});
	};
};

export const omit = (obj, keys) => Object.fromEntries(Object.entries(obj).filter(([k]) => !keys.includes(k)));

export const uniqBy = (a, key) => {
	let seen = new Set();
	return a.filter((item) => {
		let k = key(item);
		return seen.has(k) ? false : seen.add(k);
	});
};

export const groupBy = (objArray = [], prop = "") =>
	objArray.reduce((acc, obj) => {
		const key = obj[prop];
		if (!acc[key]) acc[key] = [];
		acc[key].push(obj);
		return acc;
	}, {});

export const shuffleArray = (arr) => {
	const newArr = arr.slice();
	for (let i = newArr.length - 1; i > 0; i--) {
		const rand = Math.floor(Math.random() * (i + 1));
		[newArr[i], newArr[rand]] = [newArr[rand], newArr[i]];
	}
	return newArr;
};

export const flattenWithChild = (list) =>
	flattenDeep(
		list?.map((listItem) => [
			listItem?.id,
			...(listItem?.has_children ? flattenWithChild(listItem?.children?.data) : []),
		])
	);

export const settingCategoryWithAllCategoryChildren = (list) =>
	list?.map((listItem) => [
		listItem,
		...(listItem?.has_children && listItem?.children?.data?.some((item) => item?.is_in_menu)
			? settingCategoryWithAllCategoryChildren(listItem?.children?.data)
			: []),
	]);

export const percentage = (val, total, opts = {}) => {
	const { decimal = 0, percentageSign = false } = opts;

	if (typeof val == "number") {
		if (total) {
			if (typeof total == "number" && total === parseInt(total)) {
				return `${((val / total) * 100).toFixed(decimal)}${percentageSign ? "%" : ""}`;
			} else {
				return false;
			}
		} else {
			return `${(Number(val) * 100).toFixed(decimal)}${percentageSign ? "%" : ""}`;
		}
	} else if (typeof val === "string" && val.toLowerCase() === "infinity") {
		return "∞";
	} else {
		return false;
	}
};

export const reviewsStatisticProgress = (rating) => {
	// get all keys from rating parameter object.
	let ratingKeys = Object.keys(rating);
	// defining reducer function, used in calculating total sum.
	let reducerFn = (accumulator, currentValue) => accumulator + rating[currentValue];
	// get total sum using reducer function.
	let ratingSum = ratingKeys?.length
		? ratingKeys?.map((rate) => Number(rate))?.reduce(reducerFn, rating[ratingKeys[0]])
		: ratingKeys;
	// defining final rating object.
	let ratingObj = ratingKeys.map((key) => ({
		total: ratingSum,
		current: rating[key],
		percentage: percentage(rating[key], ratingSum, { percentageSign: true }),
	}));
	// returning the rating object.
	return ratingObj;
};

export const arrayRange = (start, stop, step = 1) =>
	Array(Math.ceil((stop - start) / step))
		.fill(start)
		.map((x, y) => x + y * step);

export const splitArrayInHalf = (list = []) =>
	list?.length ? [list.slice(0, Math.ceil(list.length / 2)), list.slice(Math.ceil(list.length / 2))] : [];

export const splitArrayInHalfUsingOrder = (list) =>
	list?.length
		? [
				list?.filter(({ sort_order }) => Boolean(sort_order % 2)),
				list?.filter(({ sort_order }) => !Boolean(sort_order % 2)),
		  ]
		: [];

export const toFixed = (value = 0, rounded = 0) => {
	const str = String(value);
	const isNegative = str.startsWith("-");
	const normalized = isNegative ? str.slice(1) : str;
	const dotIndex = normalized.indexOf(".");
	const intPart = dotIndex === -1 ? normalized : normalized.slice(0, dotIndex);
	const decPart = dotIndex === -1 ? "" : normalized.slice(dotIndex + 1);
	const decimalLimit = rounded || -1;

	if (decimalLimit < 0) {
		if (!decPart) return isNegative ? `-${intPart}` : intPart;
		return isNegative ? `-${intPart}.${decPart}` : `${intPart}.${decPart}`;
	}

	const cappedDec = decPart.slice(0, Math.min(Math.floor(Number(decimalLimit)), 20));
	if (!cappedDec) return isNegative ? `-${intPart}` : intPart;
	return isNegative ? `-${intPart}.${cappedDec}` : `${intPart}.${cappedDec}`;
};

export const toCapitalCase = (str = "") => str.charAt(0).toUpperCase() + str.slice(1);

export const formatStatusText = (text = "") => {
	if (!text) return "";
	return text
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

export const isDefaultLocale = (locale = "") =>
	locale.toLowerCase().split("-")[0] === (vars?.app?.defaultLocale || "").toLowerCase();

export const getRouteLocale = (language = "") =>
	!isDefaultLocale(language) ? `/${language.toLowerCase().split("-")[0]}` : "";

export const isObject = (value) => typeof value === "object" && !Array.isArray(value) && value !== null;

export const isFunction = (value) => typeof value === "function";

export const stringToFriendlyURLString = (value = "", includeCharacters = [".", "-", "_"]) => {
	const allowed = new Set([
		...Array.from({ length: 26 }, (_, index) => String.fromCharCode(97 + index)),
		...Array.from({ length: 10 }, (_, index) => String(index)),
		...(includeCharacters || []).filter((char) => typeof char === "string" && char.length === 1),
	]);

	let newValue = value
		.toLowerCase()
		.split("")
		.filter((char) => allowed.has(char))
		.join("");

	includeCharacters?.forEach((char) => {
		newValue = newValue.split(char).filter(Boolean).join(char).trim();
	});
	return encodeURIComponent(newValue);
};

export const fileImage = (file) => (file?.type?.match(/image.*/) ? window.URL.createObjectURL(file) : false);

export const isNewerVersion = (oldVer = "", newVer = "") => {
	// check if versions passed to the function.
	if (!oldVer || !newVer) return false;
	// Check if the versions have the same number of parts.
	const partsCount = oldVer.split(".").length;
	if (partsCount !== newVer.split(".").length) {
		// If they don't, then the version with more parts is newer.
		return partsCount > newVer.split(".").length;
	}

	// If they have the same number of parts, then compare each part.
	for (var i = 0; i < partsCount; i++) {
		const a = ~~newVer.split(".")[i]; // parse int
		const b = ~~oldVer.split(".")[i]; // parse int
		if (a > b) {
			// If any part is greater, then the new version is newer.
			return true;
		} else if (a < b) {
			// If any part is less, then the old version is newer.
			return false;
		}
	}

	// If all of the parts are equal, then the versions are the same.
	return false;
};

export const getOrdinal = (n) => {
	// Check if the parameter is a number.
	if (isNaN(n)) throw new Error(`parameter must be in number format!`);
	// Return the ordinal suffix.
	if (n % 10 === 1 && n % 100 !== 11) return `${n}st`;
	if (n % 10 === 2 && n % 100 !== 12) return `${n}nd`;
	if (n % 10 === 3 && n % 100 !== 13) return `${n}rd`;
	return `${n}th`;
};

export const slugify = (str = "", separator = "-") =>
	str
		.toString()
		.normalize("NFD") // split an accented letter in the base letter and the acent
		.replace(/[\u0300-\u036f]/g, "") // remove all previously split accents
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9 ]/g, "") // remove all chars not letters, numbers and spaces (to be replaced)
		.replace(/\s+/g, separator);

export const removeDuplicates = (arr = []) => arr.filter((item, index) => arr.indexOf(item) === index);

export const splitHostNameToArray = () => [...(window.location.hostname.split(".") || [])];

/**
 * Extract tenant from current domain (hostname).
 * e.g. taptypehospital.taptype-fe.test → "taptypehospital", patients.localhost → "patients".
 * Returns null when hostname has no subdomain (e.g. localhost) or first segment is reserved (www, app, admin).
 */
export const getTenantFromDomain = () => {
	const hostName = typeof window !== "undefined" ? window.location?.hostname || "" : "";
	const parts = hostName.split(".").filter(Boolean);
	if (parts.length < 2) return null;
	const first = (parts[0] || "").trim();
	const reserved = ["www", "app", "admin"];
	if (!first || reserved.includes(first.toLowerCase())) return null;
	return first;
};

export const isSubdomain = () => {
	const domainArray = splitHostNameToArray();
	const firstElementAtDomainArray = domainArray?.[0] || "";
	const secondLastElementAtDomainArray = domainArray.slice(-2)?.[0] || "";
	const subdomainArrayFiltered = removeDuplicates(
		[
			"www",
			"admin",
			"app",
			secondLastElementAtDomainArray,
			...((!vars.isProduction &&
				vars.app.allowLocalHostToMultiTenantDomains && ["localhost", vars.app.localSubdomain]) ||
				[]),
		].filter(Boolean)
	);

	return !subdomainArrayFiltered.includes(firstElementAtDomainArray);
};

export const getTenant = () => {
	if (!vars.isProduction && vars.app.allowLocalHostToMultiTenantDomains) {
		// Local only: use tenant from URL when hostname has subdomain (e.g. taptypehospital.taptype-fe.test)
		const fromDomain = getTenantFromDomain();
		if (fromDomain) return fromDomain;
		return vars.app.localSubdomain || vars.patients.tenantName || "";
	}
	const domainArray = splitHostNameToArray();
	return domainArray?.[0] || "";
};

export const isJustmedsTenant = () => getTenant().toLowerCase() === vars.justmeds.tenantName;

export const isPatientTenant = () => getTenant().toLowerCase() === vars.patients.tenantName;

export const isAppTenant = () => getTenant().toLowerCase() === "app";

// isEmailOnlyPatientVerification() lived here and hard-disabled SMS on patients.*
// and app.*, which is every host a patient actually registers from - so the
// channel was decided by hostname and SMS was unreachable. Whether SMS can be
// sent depends on the tenant's quota and the kill switch, neither of which the
// browser can know, so the API answers it with `sms_available` instead.

export const isOpenedInIframe = () => window.location !== window.parent.location;

export const isEmpty = (obj) => [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length;

export const isOdd = (num) => num % 2;

export const getIn = (obj, path, defaultValue = undefined) => {
	const travel = (regexp) =>
		String.prototype.split
			.call(path, regexp)
			.filter(Boolean)
			.reduce((res, key) => (res !== null && res !== undefined ? res[key] : res), obj);
	const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/);
	return result === undefined || result === obj ? defaultValue : result;
};

/**
 * Hands the browser a file it did not navigate to.
 *
 * The object URL is revoked on a timer rather than straight after the click: Firefox and Safari
 * read the blob asynchronously, and revoking in the same task kills the download before it starts
 * — silently, since the click itself never fails.
 */
export const saveBlobAsFile = (blob, filename = "download") => {
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.rel = "noopener noreferrer";
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.setTimeout(() => window.URL.revokeObjectURL(url), 120_000);
};

export const countDownTimer = (date) => {
	var distance = new Date(date).getTime() - new Date().getTime();
	var _second = 1000;
	var _minute = _second * 60;
	var _hour = _minute * 60;
	var _day = _hour * 24;
	if (distance < 0) return { seconds: 0, minutes: 0, hours: 0, days: 0 };
	return {
		seconds: Math.floor((distance % _minute) / _second),
		minutes: Math.floor((distance % _hour) / _minute),
		hours: Math.floor((distance % _day) / _hour),
		days: Math.floor(distance / _day),
	};
};

export const calculateAgeFromBirthDate = (birthDate = "", dateFormat = "DD/MM/YYYY") => {
	// Return null for redacted or missing values so the UI does not throw.
	if (birthDate == null || String(birthDate).trim() === "" || String(birthDate).trim().toUpperCase() === "REDACTED") {
		return null;
	}
	const m = parsePatientDateOfBirthMoment(birthDate, dateFormat);
	if (!m.isValid()) {
		return null;
	}
	const day = m.date();
	const month = m.month() + 1;
	const year = m.year();

	const today = new Date();
	const currentYear = today.getFullYear();
	const currentMonth = today.getMonth() + 1; // Months are 0-indexed
	const currentDate = today.getDate();

	let ageInYears = currentYear - year;
	let remainingMonths = 0;
	let remainingDays = 0;

	if (currentMonth > month || (currentMonth === month && currentDate >= day)) {
		remainingMonths = currentMonth - month;
	} else {
		ageInYears--; // If birth month hasn't passed yet, subtract a year
		remainingMonths = 12 - month + currentMonth;
	}

	if (currentDate >= day) {
		remainingDays = currentDate - day;
	} else {
		remainingMonths--; // If birth day hasn't passed yet, subtract a month
		remainingDays = new Date(currentYear, currentMonth - 1, 0).getDate() - day + 1;
	}

	return {
		years: ageInYears,
		months: remainingMonths,
		days: remainingDays,
	};
};

export const hasJsonStructure = (str) => {
	if (typeof str !== "string") return false;
	try {
		const result = JSON.parse(str);
		const type = Object.prototype.toString.call(result);
		return type === "[object Object]" || type === "[object Array]";
	} catch (error) {
		return false;
	}
};

export const filterObjectFalsyValues = (obj) =>
	Object.keys(obj).reduce((acc, key) => {
		if (obj[key]) acc[key] = obj[key];
		return acc;
	}, {});

export const getCurrentSubdomain = (userTenantName) => {
	const hostName = window?.location?.hostname || "";
	const hostNameSplitted = hostName.split(".");
	const tenantName =
		[...(hostNameSplitted || [])].reverse()?.[1] !== hostNameSplitted?.[0] &&
		hostNameSplitted?.[0] !== "www" &&
		hostNameSplitted?.[0] !== "app" &&
		!hostNameSplitted.includes("admin");

	if (!vars.isProduction && vars.app.allowLocalHostToMultiTenantDomains) {
		// Local only: use tenant from URL when hostname has subdomain (e.g. taptypehospital.taptype-fe.test)
		const fromDomain = getTenantFromDomain();
		if (fromDomain) return fromDomain;
		return userTenantName || vars.app.localSubdomain || vars.patients?.tenantName || "";
	}

	if (vars.isProduction && (userTenantName || tenantName)) return userTenantName || hostNameSplitted?.[0] || null;

	return null;
};

export const sortArrayByAttribute = ({ list = [], attribute, order = "asc" }) => {
	if (!attribute) throw new Error("attribute param mustn't be empty!");
	if (!Array.isArray(list)) throw new Error("list parameter must be an array!");
	return [...list].sort((a, b) => {
		const comparison = a[attribute].localeCompare(b[attribute]);
		return order === "asc" ? comparison : -comparison;
	});
};

export const numberWithCommas = (number = 0) => number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * Formats an array of strings into a string with comma-separated elements
 * and a count for remaining elements.
 *
 * @param {string[]} items - An array of strings.
 * @throws {Error} Throws an error if the parameter is not an array.
 * @returns {string} A formatted string with the first two elements comma-separated and a count for the remaining elements (if any).
 */
export const formatList = (items) => {
	// Check if the parameter is an array
	if (!Array.isArray(items)) {
		throw new Error("items parameter must be an array!");
	}

	// If the array is empty, return an empty string
	if (items.length === 0) {
		return "";
	}

	// Get the first two elements of the array and join them with a comma
	const firstTwoItems = items.slice(0, 2).join(", ");

	// Calculate the count of remaining elements
	const remainingCount = items.length - 2;

	// Return the formatted string
	return `${firstTwoItems}${remainingCount > 0 ? `, and ${remainingCount} more` : ""}`;
};

export const formatNumberMoreThanWithPlus = (number = 0, maxNumber = 10) => {
	if (typeof number !== "number" || typeof maxNumber !== "number") {
		throw new Error("Both parameters must be numbers");
	}
	if (maxNumber <= 0) {
		throw new Error("maxNumber must be greater than zero");
	}
	return number < maxNumber ? number : `+${maxNumber - 1}`;
};

export const downloadPdf = (url, fileName = "new_file.pdf") => {
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;
	link.target = "_blank";
	link.rel = "noopener noreferrer";

	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

/**
 * Fetches a remote PDF as a blob and triggers a file download via object URL (preferred).
 * If fetch fails (e.g. missing CORS on the file host), falls back like `downloadPdf`:
 * `download` is ignored for cross-origin URLs — without `target="_blank"` the browser would
 * navigate the **current** window to the PDF and unload the app.
 *
 * @param {string} url
 * @param {string} [fileName]
 */
export const downloadRemotePdfFile = async (url, fileName = "document.pdf") => {
	const u = typeof url === "string" ? url.trim() : "";
	if (!u) return;

	try {
		const res = await fetch(u, { mode: "cors", credentials: "omit", redirect: "follow" });
		if (!res.ok) throw new Error("fetch failed");
		const blob = await res.blob();
		const blobUrl = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = blobUrl;
		link.download = fileName;
		link.rel = "noopener noreferrer";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120_000);
	} catch (_e) {
		const link = document.createElement("a");
		link.href = u;
		link.download = fileName;
		link.target = "_blank";
		link.rel = "noopener noreferrer";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}
};

/**
 * Opens a remote PDF after async work (e.g. Skyflow fetch). Do not use `noopener` on
 * `window.open("about:blank")` — with `noopener` many browsers return `null` while still opening a
 * tab; the opener cannot navigate that tab, so it stays blank until a second click.
 *
 * Prefer fetching the PDF as a blob (same-origin blob URL) then navigating the pre-opened tab
 * (works after `await`). Falls back to direct URL navigation, then `window.open` / `<a target="_blank">`.
 */
export const openRemotePdfAfterUserGesture = async (url, preOpenedWindow = null) => {
	const u = typeof url === "string" ? url.trim() : "";
	if (!u) return;

	const navigatePreOpened = (targetUrl) => {
		if (preOpenedWindow && !preOpenedWindow.closed) {
			try {
				preOpenedWindow.location.href = targetUrl;
				return true;
			} catch (_e) {
				try {
					preOpenedWindow.close();
				} catch (_e2) {}
			}
		}
		return false;
	};

	const openFallback = () => {
		if (typeof window === "undefined") return;
		const w = window.open(u, "_blank", "noopener,noreferrer");
		if (w) return;
		const link = document.createElement("a");
		link.href = u;
		link.target = "_blank";
		link.rel = "noopener noreferrer";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	try {
		const res = await fetch(u, { mode: "cors", credentials: "omit" });
		if (!res.ok) throw new Error("fetch failed");
		const blob = await res.blob();
		const blobUrl = URL.createObjectURL(blob);
		if (!navigatePreOpened(blobUrl) && typeof window !== "undefined") {
			window.open(blobUrl, "_blank", "noopener,noreferrer");
		}
		window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120_000);
		return;
	} catch (_e) {
		if (navigatePreOpened(u)) return;
		openFallback();
	}
};

/**
 * Picks specific properties from an object and returns a new object containing those properties.
 *
 * @param {Object} object - The source object to pick properties from.
 * @param {string[]} keys - An array of keys to select from the object.
 * @throws {Error} Throws an error if the object is null or undefined.
 * @throws {Error} Throws an error if keys is not an array.
 * @throws {Error} Throws an error if a key does not exist on the object.
 * @returns {Object} A new object containing only the specified keys from the original object.
 */
export const pick = (object, keys) => {
	// Check if the object is null or undefined
	if (!object) {
		throw new Error("pick() method was called with a null or undefined object");
	}
	// Check if keys is an array
	if (!Array.isArray(keys)) {
		throw new Error("pick() method was called with a non-array as the 'keys' parameter");
	}
	// Use reduce to build a new object with the specified keys
	return keys.reduce((obj, key) => {
		// Check if the key exists in the object
		if (object.hasOwnProperty(key)) {
			// Assign the value to the new object
			obj[key] = object[key];
		}
		return obj;
	}, {});
};

export const getParentIds = (obj) => {
	const ids = [];
	let current = obj.parent;

	while (current && current.id) {
		ids.push(current.id);
		current = current.parent;
	}

	return ids;
};

export const toSnakeCase = (str = "") =>
	str
		// Insert underscore between lowercase and uppercase: "testCase" → "test_Case"
		.replace(/([a-z0-9])([A-Z])/g, "$1_$2")
		// Replace all spaces, hyphens, and multiple underscores with a single underscore
		.replace(/[\s-]+/g, "_")
		// Remove any leading or trailing underscores
		.replace(/^_+|_+$/g, "")
		// Convert to lowercase
		.toLowerCase();

export const roundUpToNextHalfHour = (timeStr = "", interval = 30) => {
	const m = moment(timeStr, "HH:mm:ss").subtract(interval, "minute");

	const minutes = m.minutes();
	const seconds = m.seconds();

	// Clear seconds and milliseconds
	m.seconds(0).milliseconds(0);

	if (minutes === 0 && seconds === 0) return m.format("HH:mm:ss");

	if (minutes <= interval) {
		m.minutes(interval);
	} else {
		m.add(1, "hour").minutes(0);
	}

	return m.format("HH:mm:ss");
};
